"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  unstakeSharedAction,
  stakeSharedAction,
  confirmStakeUpdateHashAction,
  confirmUnstakeRequestAction,
  confirmUnstakeWithdrawAction,
  cancelUnstakeAction,
} from "@/resources/actions/user.actions";
import { queryKeys } from "@/lib/query-keys";
import type {
  UnstakeSharedRequest,
  StakeSharedRequest,
  ConfirmStakeUpdateHashRequest,
  ConfirmUnstakeRequest,
  ConfirmUnstakeWithdrawRequest,
  CancelUnstakeRequest,
} from "@/resources/interfaces/user.interface";

/**
 * Mutation hook — initiate an unstake from a shared pool.
 * On success, both stakeHistory and unstakeHistory caches are invalidated
 * for the given wallet so the UI refreshes automatically.
 */
export function useUnstakeShared() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UnstakeSharedRequest) => {
      const result = await unstakeSharedAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.stakeHistory(variables.wallet),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.unstakeHistory(variables.wallet),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.summary(variables.wallet),
      });
    },
  });
}

/**
 * Mutation hook — stake shared tokens.
 * On success, stake-related caches are invalidated
 * for the given wallet so the UI refreshes automatically.
 */
export function useStakeShared() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StakeSharedRequest) => {
      const result = await stakeSharedAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.stakeHistory(variables.wallet),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.summary(variables.wallet),
      });
    },
  });
}

/**
 * Mutation hook — confirm stake update hash.
 * On success, the stakeHistory cache is invalidated
 * for the given wallet so the UI refreshes automatically.
 */
export function useConfirmStakeUpdateHash() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConfirmStakeUpdateHashRequest) => {
      const result = await confirmStakeUpdateHashAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.stakeHistory(variables.wallet),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedStaking.summary(variables.wallet),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.userDashboard.statistics(variables.wallet),
      });
    },
  });
}

/**
 * Mutation hook — confirm unstake request tx.
 * On success, shared staking caches are invalidated for returned wallet.
 */
export function useConfirmUnstakeRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConfirmUnstakeRequest) => {
      const result = await confirmUnstakeRequestAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data) => {
      if (_data?.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.unstakeHistory(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.summary(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.stats(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.statistics(_data.wallet),
        });
      }
    },
  });
}

/**
 * Mutation hook — confirm final unstake withdraw tx.
 * On success, shared staking caches are invalidated for returned wallet.
 */
export function useConfirmUnstakeWithdraw() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConfirmUnstakeWithdrawRequest) => {
      const result = await confirmUnstakeWithdrawAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data) => {
      if (_data?.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.unstakeHistory(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.summary(_data.wallet),
        });
      }
    },
  });
}

/**
 * Mutation hook — cancel an unstake request that is in the unbonding period.
 * On success, unstake history and summary caches are invalidated for the wallet.
 */
export function useCancelUnstake() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CancelUnstakeRequest) => {
      const result = await cancelUnstakeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data) => {
      if (_data?.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.unstakeHistory(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.summary(_data.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.sharedStaking.stats(_data.wallet),
        });
      }
    },
  });
}
